import CryptoJS from 'crypto-js';
import NodeRSA from 'node-rsa';

/**
 * @typedef {Object} EncryptProxyClientOptions
 * @property {string} baseURL - 服务端基础URL，用于请求公钥和初始化会话（例如: https://api.example.com）
 * @property {string[]} [whitelistUrlPatterns] - 请求 URL 白名单正则表达式数组；匹配会跳过加密/解密处理
 * @property {string[]} [whitelistDataTypes] - 请求数据类型白名单数组，填写数据构造函数名（例如 'FormData'、'URLSearchParams'）
 * @property {boolean} [debug=false] - 是否启用调试模式，启用后会打印调试日志
 * @property {number} [sessionRefreshThreshold=1500000] - 会话刷新阈值，单位毫秒，默认 25 分钟（25*60*1000）
 */

/**
 * 浏览器加密代理客户端工具类
 * 用于配合服务端的RSA+AES混合加密机制
 */

export class EncryptProxyClient {
  baseURL
  sessionId
  aesKey
  publicKey
  whitelist
  debug
  sessionRefreshThreshold
  lastSessionInitTime
  requestInterceptor
  responseInterceptor
  // 等待锁promise，会话未生效时进行promise等待,用于限制全局只允许有一个initSession的行为
  initSessionWaitList
  // 是否正在初始化会话
  isInitializing
  /**
   * Create an EncryptProxyClient.
   * @param {EncryptProxyClientOptions} [options] - 配置项
   */
  constructor(options = {}) {
    this.baseURL = options.baseURL || '';
    this.sessionId = null;
    this.aesKey = null;
    this.publicKey = null;
    this.initSessionWaitList = [];
    this.isInitializing = false;

    // 白名单配置
    this.whitelist = {
      urlPatterns: options.whitelistUrlPatterns || [],
      dataTypes: options.whitelistDataTypes || []
    };

    // 调试模式
    this.debug = options.debug || false;

    // 会话刷新阈值
    this.sessionRefreshThreshold = options.sessionRefreshThreshold || 25 * 60 * 1000;

    // axios拦截器引用
    this.requestInterceptor = null;
    this.responseInterceptor = null;
  }

  /**
   * 记录调试日志
   */
  log(message, data = null) {
    if (this.debug) {
      console.log(`[EncryptProxyClient] ${message}`, data);
    }
  }

  /**
   */
  encryptWithRSA(data, publicKeyPem) {
    try {
      // NodeRSA 在浏览器中可通过打包器使用（webpack/vite 等）
      // 使用 PKCS#8 公钥作为首选格式，若失败则回退到 PKCS#1
      const key = new NodeRSA();

      try {
        key.importKey(publicKeyPem, 'pkcs8-public-pem');
      } catch (e) {
        // 回退尝试
        try {
          key.importKey(publicKeyPem, 'pkcs1-public-pem');
        } catch (ee) {
          // 最后尝试直接让构造函数解析
          try {
            const keyDirect = new NodeRSA(publicKeyPem);
            const encryptedDirect = keyDirect.encrypt(data, 'base64');
            if (!encryptedDirect) throw new Error('RSA加密失败');
            this.log('RSA加密成功(直接解析)');
            return encryptedDirect;
          } catch (finalErr) {
            this.log('RSA导入公钥失败', finalErr);
            throw finalErr;
          }
        }
      }

      const encrypted = key.encrypt(data, 'base64');
      if (!encrypted) {
        throw new Error('RSA加密失败');
      }

      this.log('RSA加密成功');
      return encrypted;
    } catch (error) {
      this.log('RSA加密失败', error);
      throw new Error('RSA加密失败');
    }
  }



  /**
   * 获取服务端RSA公钥
   */
  async getPublicKey() {
    if (this.publicKey) {
      return this.publicKey;
    }

    try {
      const response = await fetch(`${this.baseURL}/api/public-key`);
      const result = await response.json();

      if (!result.success) {
        throw new Error('获取公钥失败');
      }

      this.publicKey = result.data.publicKey;
      this.log('获取公钥成功');
      return this.publicKey;
    } catch (error) {
      this.log('获取公钥失败', error);
      throw error;
    }
  }

  /**
   * 生成随机AES密钥
   */
  generateAESKey() {
    return CryptoJS.lib.WordArray.random(128/8).toString();
  }

  /**
   * 使用AES密钥加密数据
   */
  encryptWithAES(data) {
    try {
      let dataUse;
      // 判断data是json还是URLSearchParams，都统一转换为JSON字符串进行加密
      if (data instanceof URLSearchParams) {
        dataUse = Object.fromEntries(data.entries());
      } else {
        dataUse = data;
      }
      return CryptoJS.AES.encrypt(JSON.stringify(dataUse), this.aesKey).toString();
    } catch (error) {
      this.log('AES加密失败', error);
      throw new Error('AES加密失败');
    }
  }

  /**
   * 使用AES密钥解密数据
   */
  decryptWithAES(encryptedData) {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedData, this.aesKey);
      const decryptedStr = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decryptedStr);
    } catch (error) {
      this.log('AES解密失败', error.message);
      throw new Error('AES解密失败');
    }
  }

  /**
   * 初始化加密会话
   */
  async initSession() {
    return new Promise((resolve, reject) => {
      // 如果当前正在初始化，将promise的resolve和reject加入等待列表
      if (this.isInitializing) {
        this.log('会话正在初始化中，加入等待队列');
        this.initSessionWaitList.push({ resolve, reject });
        return;
      }

      // 如果会话已经有效，直接返回成功
      if (this.isSessionValid()) {
        this.log('会话已存在，直接返回');
        resolve();
        return;
      }

      // 标记为正在初始化
      this.isInitializing = true;
      this.log('开始初始化加密会话');

      // 执行实际的初始化逻辑
      this._performInitSession()
        .then(() => {
          // 初始化成功，解析当前promise
          resolve();

          // 解析所有等待中的promise
          this._resolveAllWaitingPromises();
        })
        .catch(error => {
          // 初始化失败，拒绝当前promise
          reject(error);

          // 拒绝所有等待中的promise
          this._rejectAllWaitingPromises(error);
        })
        .finally(() => {
          // 重置初始化状态和等待列表
          this.isInitializing = false;
          this.initSessionWaitList = [];
        });
    });
  }

  /**
   * 执行实际的会话初始化逻辑
   * @private
   */
  async _performInitSession() {
    try {
      // 获取公钥
      const publicKey = await this.getPublicKey();
      // 生成AES密钥
      const aesKey = this.generateAESKey();
      this.aesKey = aesKey;

      // 使用RSA加密AES密钥
      const encryptedAESKey = this.encryptWithRSA(aesKey, publicKey);
      // 发送会话初始化请求
      const response = await fetch(`${this.baseURL}/api/init-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ encryptedAESKey })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || '会话初始化失败');
      }

      // 解密会话ID
      const decryptedSession = this.decryptWithAES(result.data.encryptedSessionId);
      this.sessionId = decryptedSession.sessionId;
      this.lastSessionInitTime = Date.now();

      this.log('加密会话初始化成功', { sessionId: this.sessionId });
    } catch (error) {
      this.log('加密会话初始化失败:', error.message);
      throw error;
    }
  }

  /**
   * 解析所有等待中的promise
   * @private
   */
  _resolveAllWaitingPromises() {
    const waitingCount = this.initSessionWaitList.length;
    if (waitingCount > 0) {
      this.log(`解析 ${waitingCount} 个等待中的会话初始化请求`);
      this.initSessionWaitList.forEach(({ resolve }) => {
        try {
          resolve();
        } catch (error) {
          this.log('解析等待中的promise时出错', error);
        }
      });
    }
  }

  /**
   * 拒绝所有等待中的promise
   * @private
   */
  _rejectAllWaitingPromises(error) {
    const waitingCount = this.initSessionWaitList.length;
    if (waitingCount > 0) {
      this.log(`拒绝 ${waitingCount} 个等待中的会话初始化请求`);
      this.initSessionWaitList.forEach(({ reject }) => {
        try {
          reject(error);
        } catch (rejectionError) {
          this.log('拒绝等待中的promise时出错', rejectionError);
        }
      });
    }
  }

  /**
   * 检查会话是否有效
   */
  isSessionValid() {
    return !!(this.sessionId && this.aesKey);
  }

  /**
   * 刷新加密会话
   */
  async refreshSession() {
    this.log('刷新加密会话');
    this.sessionId = null;
    this.aesKey = null;
    this.publicKey = null;
    await this.initSession();
  }

  /**
   * 检查URL是否在白名单中
   */
  isUrlInWhitelist(url) {
    return this.whitelist.urlPatterns.some(pattern => {
      try {
        const regex = new RegExp(pattern);
        return regex.test(url);
      } catch (error) {
        this.log(`白名单正则表达式无效: ${pattern}`, error);
        return false;
      }
    });
  }

  /**
   * 检查数据类型是否在白名单中
   */
  isDataTypeInWhitelist(data) {
    const dataType = data?.constructor?.name;
    return this.whitelist.dataTypes.includes(dataType);
  }

  /**
   * 检查请求是否应该跳过加密
   */
  shouldSkipEncryption(url, data) {
    if (this.isUrlInWhitelist(url)) {
      this.log(`URL在白名单中，跳过加密: ${url}`);
      return true;
    }

    if (this.isDataTypeInWhitelist(data)) {
      this.log(`数据类型在白名单中，跳过加密: ${data?.constructor?.name}`);
      return true;
    }

    return false;
  }

  /**
   * 处理请求数据加密
   * @param {any} data - 要加密的数据
   * @param {string} url - 请求 URL
   * @param {Object} [headers] - 请求时的 headers（来自 axios config.headers）
   */
  async encryptRequestData(data, url, headers) {
    // 跳过加密
    if (this.shouldSkipEncryption(url, data)) {
      return data;
    }

    // 确保会话已初始化
    if (!this.isSessionValid()) {
      await this.initSession();
    }

    // 检查是否需要刷新会话
    if (this.lastSessionInitTime &&
        Date.now() - this.lastSessionInitTime > this.sessionRefreshThreshold) {
      await this.refreshSession();
    }

    try {
      const encryptedData = this.encryptWithAES(data);
      this.log('请求数据加密成功:', data);

      return {
        sessionId: this.sessionId,
        encryptedData
      };
    } catch (error) {
      this.log('请求数据加密失败', error);
      throw error;
    }
  }

  /**
   * 处理响应数据解密
   * @param {any} data - axios response.data
   * @param {string} url - 请求 URL
   * @param {Object} [headers] - 请求时的 headers（来自 axios config.headers）
   * @param {number} [status] - HTTP响应状态码
   */
  async decryptResponseData(data, url, headers, status) {
    console.log('decryptResponseData', data)
    // 白名单URL直接返回
    if (this.isUrlInWhitelist(url)) {
      return data;
    }

    // 检查响应格式
    if (!data) {
      return data;
    }

    // 处理SESSION_INVALID情况，需要重新建立会话
    if (data.info === 'SESSION_INVALID') {
      this.log('会话无效，重新初始化会话');
      await this.refreshSession();
      return data;
    }

    // 如果不包含encrypted:true，那么就返回data
    if (data.encrypted !== true) {
      return data;
    }


    try {
      if (!data.data) {
        throw new Error('响应数据格式不正确，缺少data字段');
      }
      const decryptedData = this.decryptWithAES(data.data);
      this.log('响应数据解密成功', decryptedData);
      return decryptedData;
    } catch (error) {
      this.log('响应数据解密失败，可能响应未加密', error);
      return data;
    }
  }

  /**
   * 重置会话
   */
  resetSession() {
    this.sessionId = null;
    this.aesKey = null;
    this.lastSessionInitTime = null;
    this.isInitializing = false;
    this.initSessionWaitList = [];
    this.log('会话已重置');
  }

  /**
   * 配置axios请求拦截器
   * @param {import('axios').AxiosInstance} axiosInstance - axios实例
   */
  setupRequestInterceptor(axiosInstance) {
    if (!axiosInstance) {
      throw new Error('axios实例不能为空');
    }

    this.requestInterceptor = axiosInstance.interceptors.request.use(
      async (config) => {
        this.log('拦截请求', { url: config.url, method: config.method });

        if (config.data) {
          try {
            // 将请求 headers 传入以供加密逻辑或业务判断使用
            config.data = await this.encryptRequestData(
              config.data,
              config.url || '',
              config.headers || {}
            );
          } catch (error) {
            this.log('请求加密处理失败', error);
            throw error;
          }
        }

        return config;
      },
      (error) => {
        this.log('请求拦截器错误', error);
        return Promise.reject(error);
      }
    );

    this.log('axios请求拦截器配置完成');
  }

  /**
   * 配置axios响应拦截器
   * @param {import('axios').AxiosInstance} axiosInstance - axios实例
   */
  setupResponseInterceptor(axiosInstance) {
    if (!axiosInstance) {
      throw new Error('axios实例不能为空');
    }

    this.responseInterceptor = axiosInstance.interceptors.response.use(
      async (response) => {
        this.log('拦截响应', { url: response.config.url, status: response.status });

        if (response.data) {
          try {
            // 将请求 headers 传入以便解密逻辑或业务判断使用
            const decryptedData = await this.decryptResponseData(
              response.data,
              response.config.url || '',
              response.config?.headers || {},
              response.status
            );
            try {
              response.data = JSON.parse(decryptedData);
            } catch (err) {
              response.data = decryptedData;
            }
          } catch (error) {
            this.log('响应解密处理失败', error);
          }
        }

        return response;
      },
      async (error) => {
        this.log('响应拦截器错误', error);

        // 401错误尝试重新初始化会话
        if (error.response?.status === 401) {
          this.log('会话可能已过期，尝试重新初始化');
          try {
            this.resetSession();
          } catch (refreshError) {
            this.log('会话重新初始化失败', refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    this.log('axios响应拦截器配置完成');
  }

  /**
   * 配置axios拦截器（同时配置请求和响应拦截器）
   * @param {import('axios').AxiosInstance} axiosInstance - axios实例
   */
  setupAxios(axiosInstance) {
    this.setupRequestInterceptor(axiosInstance);
    this.setupResponseInterceptor(axiosInstance);
  }

  /**
   * 移除axios请求拦截器
   * @param {import('axios').AxiosInstance} axiosInstance - axios实例
   */
  removeRequestInterceptor(axiosInstance) {
    if (this.requestInterceptor !== null) {
      axiosInstance.interceptors.request.eject(this.requestInterceptor);
      this.requestInterceptor = null;
      this.log('axios请求拦截器已移除');
    }
  }

  /**
   * 移除axios响应拦截器
   * @param {import('axios').AxiosInstance} axiosInstance - axios实例
   */
  removeResponseInterceptor(axiosInstance) {
    if (this.responseInterceptor !== null) {
      axiosInstance.interceptors.response.eject(this.responseInterceptor);
      this.responseInterceptor = null;
      this.log('axios响应拦截器已移除');
    }
  }

  /**
   * 移除axios拦截器
   */
  removeAxiosInterceptors(axiosInstance) {
    if (this.requestInterceptor !== null) {
      axiosInstance.interceptors.request.eject(this.requestInterceptor);
      this.requestInterceptor = null;
    }

    if (this.responseInterceptor !== null) {
      axiosInstance.interceptors.response.eject(this.responseInterceptor);
      this.responseInterceptor = null;
    }

    this.log('axios拦截器已移除');
  }
}

export default EncryptProxyClient;
